import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { visibleScrollbar } from '../../../styledMixins';
import ScrollableArea from '../../Shared/ScrollableArea';
import { useHistory } from 'react-router-dom';
import Eye from '../../../static/icons/Eye.svg';
import MarkEdit from '../../../static/img/markEdit.svg';
import EditHover from '../../../static/img/EditHover.svg';
import plusIcon from '../../../static/img/Plus-violet.svg';
import { isTabletView } from '../../ReactiveRender';
import MenuButton from '../../MenuButton';
import ImprovedSecondarySideBar from '../../ImprovedSecondarySideBar';
import ItemSettingsModal from '../../Shared/ItemSettingsModal';
import SettingsTagsDisplay from '../../Shared/SettingsTagsDisplay';
import StepLabelBadge from '../../Shared/StepLabelBadge';
import SetupStepSection from '../../Shared/SetupStepSection';
import { useAvailableYears, useSubjects, useSubjectsForYearFromClasses, useSubjectsForYearsFromClasses, useUserStateSubjects, useTaskTypes, useResponseTypes, TASK_TYPE_LABELS, RESPONSE_TYPE_LABELS } from '../../../hooks/useSubjects';
import { useUserStates, useMarkingToolStates } from '../../../hooks/useUserStates';
import { withSelectedSubjectsPreserved } from '../../../utils/subjects';
import { TextInput, HeadingAndFilterContainer } from '../../CreateNewMarkingCriteria/CreateNewMarkingCriteriaDesktop/style';
import SuccessCriteriaDialog from '../../Shared/Dialogs/successCriteria';

function CreateNewSuccessCriteriaDesktop(props) {
  const {
    successCriteria,
    updateSuccessCriteria,
    handleTitleChange,
    handleObjectiveChange,
    handleDeleteObjective,
    handleAddObjective,
    saveSuccessCriteria,
    isUpdating,
    successCriteriaId,
  } = props;

  const history = useHistory();
  const { states: userStates } = useUserStates();
  const state = userStates;
  const [localYears, setLocalYears] = useState(null);
  const [localSubjects, setLocalSubjects] = useState(null);
  const [localTaskTypes, setLocalTaskTypes] = useState(null);
  const [localResponseTypes, setLocalResponseTypes] = useState(null);

  const selectedYears = localYears !== null ? localYears : (successCriteria?.years || []);
  const rawItemSubjects = successCriteria?.subjects || [];
  const normalizedItemSubjects = rawItemSubjects.map(s => (typeof s === 'object' && s !== null) ? (s.subjectCode || s.code || s.value || s) : s).filter(Boolean);
  const selectedSubjects = localSubjects !== null ? localSubjects : normalizedItemSubjects;
  const selectedTaskTypes = localTaskTypes !== null ? localTaskTypes : (successCriteria?.taskTypes || (successCriteria?.taskType ? [successCriteria.taskType] : []));
  const selectedResponseTypes = localResponseTypes !== null ? localResponseTypes : (successCriteria?.responseTypes || (successCriteria?.responseType ? [successCriteria.responseType] : []));

  const { years: availableYears } = useAvailableYears();
  const yearForFilter = selectedYears?.find((y) => availableYears?.includes(y)) ?? selectedYears?.[0];
  const yearsForFilter = Array.isArray(selectedYears) && selectedYears.length ? selectedYears : (yearForFilter != null ? [yearForFilter] : []);
  const scopeStates = useMarkingToolStates();
  const { subjects: subjectsByYear } = useSubjects(state, yearForFilter);
  const { subjects: subjectsFromClasses } = useSubjectsForYearFromClasses(yearForFilter, scopeStates);
  const { subjects: subjectsForSelectedYears } = useSubjectsForYearsFromClasses(selectedYears, scopeStates);
  const { subjects: allClassSubjects } = useUserStateSubjects(scopeStates);
  const subjectsForYear = (subjectsByYear?.length ? subjectsByYear : subjectsFromClasses) || [];
  const subjectsSource = selectedYears?.length
    ? (subjectsForSelectedYears?.length ? subjectsForSelectedYears : allClassSubjects)
    : [];
  const subjectsEnriched = (subjectsSource.length ? subjectsSource : subjectsForYear).map((s) => {
    const fromClass = (subjectsForSelectedYears?.length ? subjectsForSelectedYears : subjectsFromClasses)?.find((c) => c.code === s.code);
    return fromClass ? { ...s, taskTypes: fromClass.taskTypes || [], responseTypes: fromClass.responseTypes || [] } : s;
  });
  const { taskTypes: filteredTaskTypes } = useTaskTypes(selectedSubjects, state, yearsForFilter.length ? yearsForFilter : yearForFilter);
  const { responseTypes: filteredResponseTypes } = useResponseTypes(selectedSubjects, selectedTaskTypes, state, yearsForFilter.length ? yearsForFilter : yearForFilter);

  const normalizeSubjectValue = (subject) => {
    if (typeof subject === 'object' && subject !== null) {
      return subject.subjectCode || subject.code || subject.subjectName || subject.title;
    }
    return subject;
  };

  // Helper function to get display label for a subject
  const getSubjectLabel = (subject) => {
    if (typeof subject === 'object' && subject !== null) {
      return subject.subjectName || subject.name || subject.title || subject.subjectCode || subject.code;
    }
    return subject;
  };

  // Keep an edited tool's existing (possibly out-of-state) subject in the
  // options so state scoping can't silently strip it on save.
  const availableSubjects = withSelectedSubjectsPreserved(
    selectedYears?.length ? subjectsEnriched : [],
    selectedSubjects,
    rawItemSubjects,
  );
  const availableTaskTypes = selectedSubjects.length ? filteredTaskTypes : [];
  const availableResponseTypes = (selectedSubjects.length && selectedTaskTypes.length) ? filteredResponseTypes : [];

  // Auto-remove selected task types that are no longer valid
  useEffect(() => {
    if (!selectedTaskTypes.length) return;
    if (!availableTaskTypes.length) { setLocalTaskTypes([]); return; }
    const validValues = new Set(availableTaskTypes.map(t => t.value ?? t));
    const filtered = selectedTaskTypes.filter(t => validValues.has(t));
    if (filtered.length !== selectedTaskTypes.length) setLocalTaskTypes(filtered);
  }, [availableTaskTypes]);

  // Auto-remove selected response types that are no longer valid
  useEffect(() => {
    if (!selectedResponseTypes.length) return;
    if (!availableResponseTypes.length) { setLocalResponseTypes([]); return; }
    const validValues = new Set(availableResponseTypes.map(r => r.value ?? r));
    const filtered = selectedResponseTypes.filter(r => validValues.has(r));
    if (filtered.length !== selectedResponseTypes.length) setLocalResponseTypes(filtered);
  }, [availableResponseTypes]);

  const onYearsChange = (v) => {
    setLocalYears(v);
    if (!v?.length) {
      setLocalSubjects([]);
      setLocalTaskTypes([]);
      setLocalResponseTypes([]);
    }
  };
  const onSubjectsChange = (v) => {
    setLocalSubjects(v);
  };
  const onTaskTypesChange = (v) => {
    setLocalTaskTypes(v);
  };

  const [isEditing, setIsEditing] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [isShowMenu, setShowMenu] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [editingObjectiveIndex, setEditingObjectiveIndex] = useState(null);
  const [editingObjectiveText, setEditingObjectiveText] = useState('');

  const tabletView = isTabletView();

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      setIsEditing(false);
    }
  };

  const handleOnBlur = () => {
    setIsEditing(false);
  };

  const handleYearToggle = (year) => {
    setLocalYears(selectedYears.includes(year) ? selectedYears.filter((y) => y !== year) : [...selectedYears, year]);
  };

  const handleSubjectToggle = (subject) => {
    setLocalSubjects(selectedSubjects.includes(subject) ? selectedSubjects.filter((s) => s !== subject) : [...selectedSubjects, subject]);
  };

  const handleTaskTypeToggle = (type) => {
    setLocalTaskTypes(selectedTaskTypes.includes(type) ? selectedTaskTypes.filter((t) => t !== type) : [...selectedTaskTypes, type]);
  };

  const handleResponseTypeToggle = (type) => {
    setLocalResponseTypes(selectedResponseTypes.includes(type) ? selectedResponseTypes.filter((r) => r !== type) : [...selectedResponseTypes, type]);
  };

  const handleGoBack = () => {
    history.push('/tools/successcriterias');
  };

  const canSave = selectedYears.length > 0 && selectedSubjects.length > 0;

  const handleSave = () => {
    if (!canSave) return;
    saveSuccessCriteria({
      years: selectedYears,
      subjects: selectedSubjects,
      taskTypes: selectedTaskTypes,
      responseTypes: selectedResponseTypes,
    });
  };

  const handleSaveSettings = async (settings) => {
    try {
      await updateSuccessCriteria({
        years: settings.years,
        subjects: settings.subjects,
        taskTypes: settings.taskTypes,
        responseTypes: settings.responseTypes,
      });
      setLocalYears(settings.years || []);
      setLocalSubjects(settings.subjects || []);
      setLocalTaskTypes(settings.taskTypes || []);
      setLocalResponseTypes(settings.responseTypes || []);
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  const handleObjectiveClick = (index, description) => {
    setEditingObjectiveIndex(index);
    setEditingObjectiveText(description);
  };

  const handleObjectiveTextChange = (event) => {
    setEditingObjectiveText(event.target.value);
  };

  const handleObjectiveBlur = (index) => {
    if (editingObjectiveText.trim()) {
      handleObjectiveChange(index, editingObjectiveText.trim());
    }
    setEditingObjectiveIndex(null);
    setEditingObjectiveText('');
  };

  const handleObjectiveKeyPress = (event, index) => {
    if (event.key === 'Enter') {
      handleObjectiveBlur(index);
    }
  };

  const handleAddNewObjective = () => {
    handleAddObjective();
    // Set focus to new objective
    const newIndex = successCriteria.criterias[0]?.objectives?.length || 0;
    setEditingObjectiveIndex(newIndex);
    setEditingObjectiveText('');
  };

  const objectives = successCriteria.criterias?.[0]?.objectives || [];

  return (
    <>
      <MainContainer>
        <ImprovedSecondarySideBar
          isShowMenu={isShowMenu}
          setShowMenu={setShowMenu}
          id={successCriteriaId}
        />
        <ContentWrapper>
          <InnerContainer>
            {tabletView && (
              <HeadingAndFilterContainer>
                <MenuButton setShowMenu={setShowMenu} />
              </HeadingAndFilterContainer>
            )}
            <RightContainer>
              <HeadingContainer>
                <HeadingLeft>
                  {isUpdating && (
                    <BackArrow onClick={handleGoBack}>&lt;</BackArrow>
                  )}
                  {isEditing ? (
                    <Heading id="successCriteriaTitleContainer">
                      <TextInput
                        placeholder="Name of success criteria"
                        id="successCriteriaName"
                        value={successCriteria.title}
                        onChange={handleTitleChange}
                        onBlur={() => handleOnBlur()}
                        onKeyPress={handleKeyPress}
                        maxLength="100"
                      />
                    </Heading>
                  ) : (
                    <Heading onClick={() => setIsEditing(true)}>
                      {successCriteria.title || 'Title'}
                      <EditIcon src={MarkEdit} />
                      <EditIconHover src={EditHover} />
                    </Heading>
                  )}
                </HeadingLeft>
                {!isUpdating && (
                  <HeaderRight>
                    <PreviewButton onClick={() => setOpenPreviewDialog(true)}>
                      <PreviewButtonIcon src={Eye} />
                      <PreviewButtonText>Preview</PreviewButtonText>
                    </PreviewButton>
                    <SaveButton onClick={handleSave} $disabled={!canSave}>
                      <SaveButtonText>Save and Publish</SaveButtonText>
                    </SaveButton>
                  </HeaderRight>
                )}
              </HeadingContainer>
              {isUpdating && (
                <SettingsTagsRow>
                  <SettingsTagsDisplay
                    years={successCriteria?.years || []}
                    subjects={successCriteria?.metadata?.subjects || []}
                    taskTypes={successCriteria?.taskTypes || []}
                    responseTypes={successCriteria?.responseTypes || []}
                    taskTypeLabels={Object.fromEntries((availableTaskTypes ?? []).map(t => [(t && typeof t === 'object' ? t.value : t), (t && typeof t === 'object' ? t.label ?? t.title ?? t.value : t)]))}
                    responseTypeLabels={Object.fromEntries((availableResponseTypes ?? []).map(r => [(r && typeof r === 'object' ? r.value : r), (r && typeof r === 'object' ? r.label ?? r.title ?? r.value : r)]))}
                    onSettingsClick={() => setIsSettingsOpen(!isSettingsOpen)}
                    showSettingsButton={true}
                  />
                </SettingsTagsRow>
              )}

              <MainContentSection>
              {/* Step 1 - Set Up Section (ONLY for create mode) */}
              {!isUpdating && (
                <SetupStepSection
                  availableYears={availableYears}
                  availableSubjects={availableSubjects}
                  availableTaskTypes={availableTaskTypes}
                  availableResponseTypes={availableResponseTypes}
                  selectedYears={selectedYears}
                  selectedSubjects={selectedSubjects}
                  selectedTaskTypes={selectedTaskTypes}
                  selectedResponseTypes={selectedResponseTypes}
                  onYearsChange={onYearsChange}
                  onSubjectsChange={onSubjectsChange}
                  onTaskTypesChange={onTaskTypesChange}
                  onResponseTypesChange={setLocalResponseTypes}
                  getSubjectLabel={getSubjectLabel}
                  getSubjectValue={normalizeSubjectValue}
                  isUpdating={isUpdating}
                />
              )}

              {/* Step 2 - Add Criteria Section (ONLY for create mode) */}
              {!isUpdating && (
                <StepLabelRow>
                  <StepLabelBadge>Step 2 - Add Criteria</StepLabelBadge>
                </StepLabelRow>
              )}

              {/* Objectives Content */}
              <ObjectivesContainer>
                <SectionTitle>Objectives</SectionTitle>
                <ObjectivesList>
                  {objectives.map((objective, index) => (
                    <ObjectiveItem key={index}>
                      {editingObjectiveIndex === index ? (
                        <ObjectiveInput
                          type="text"
                          value={editingObjectiveText}
                          onChange={handleObjectiveTextChange}
                          onBlur={() => handleObjectiveBlur(index)}
                          onKeyPress={(e) => handleObjectiveKeyPress(e, index)}
                          autoFocus
                          placeholder="Enter objective..."
                        />
                      ) : (
                        <ObjectiveText onClick={() => handleObjectiveClick(index, objective.description)}>
                          {objective.description || 'Click to edit...'}
                        </ObjectiveText>
                      )}
                      <DeleteButton onClick={() => handleDeleteObjective(index)}>×</DeleteButton>
                    </ObjectiveItem>
                  ))}
                </ObjectivesList>
                <AddObjectiveButton onClick={handleAddNewObjective}>
                  <img src={plusIcon} alt="Add" />
                  Add New Objective
                </AddObjectiveButton>
              </ObjectivesContainer>

              {/* Bottom Buttons */}
              <BottomButtonsContainer>
                <PreviewButton onClick={() => setOpenPreviewDialog(true)}>
                  <PreviewButtonIcon src={Eye} />
                  <PreviewButtonText>Preview</PreviewButtonText>
                </PreviewButton>
                <SaveButton onClick={handleSave} $disabled={!canSave}>
                  <SaveButtonText>Save and Publish</SaveButtonText>
                </SaveButton>
              </BottomButtonsContainer>
              </MainContentSection>
            </RightContainer>
          </InnerContainer>

          {/* Settings Modal */}
          <ItemSettingsModal
            isOpen={isSettingsOpen}
            onClose={() => {
              setIsSettingsOpen(false);
              setLocalYears(null);
              setLocalSubjects(null);
              setLocalTaskTypes(null);
              setLocalResponseTypes(null);
            }}
            onSave={handleSaveSettings}
            availableYears={availableYears}
            availableSubjects={availableSubjects}
            taskTypes={availableTaskTypes}
            responseTypes={availableResponseTypes}
            selectedYears={selectedYears}
            setSelectedYears={onYearsChange}
            selectedSubjects={selectedSubjects}
            setSelectedSubjects={onSubjectsChange}
            selectedTaskTypes={selectedTaskTypes}
            setSelectedTaskTypes={onTaskTypesChange}
            selectedResponseTypes={selectedResponseTypes}
            setSelectedResponseTypes={setLocalResponseTypes}
            getSubjectLabel={getSubjectLabel}
            getSubjectValue={normalizeSubjectValue}
          />
        </ContentWrapper>
      </MainContainer>
      {openPreviewDialog && (
        <SuccessCriteriaDialog
          successCriteria={successCriteria}
          setSuccessCriteriaPreviewDialog={setOpenPreviewDialog}
          showEditButton={false}
        />
      )}
    </>
  );
}

// Styled Components
const MainContainer = styled.div`
  width: 100%;
  position: relative;
  align-items: flex-start;
  background-color: var(--white);
  display: flex;
  flex-direction: row;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100vh;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  position: relative;
`;

const RightContainer = styled(ScrollableArea).attrs({ autoHide: 'leave' })`
  width: 100%;
  background: #ffffff;
  height: 100vh;
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #c9c6cc80;
  background: #ffffff;
`;

const HeadingLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  margin-right: 8px;
`;

const BackArrow = styled.span`
  font-family: IBM Plex Sans;
  font-size: 20px;
  font-weight: 400;
  color: #7200e0;
  cursor: pointer;
  padding: 4px 8px;

  &:hover {
    opacity: 0.9;
  }
`;

const Heading = styled.p`
  font-family: IBM Plex Sans;
  font-size: 16px;
  font-weight: 500;
  line-height: 21px;
  letter-spacing: -0.01em;
  text-align: left;
  display: flex;
  align-items: center;
  padding: 8px 6px;
  gap: 4px;
  cursor: pointer;
  border: 1px solid transparent;
  margin: 0;
  flex: 1;
  min-width: 0;

  &:hover {
    border: 1px solid #c9c6cc80;
  }
`;

const EditIcon = styled.img`
  width: 20px;
  height: 20px;
  opacity: 0.85;
  flex-shrink: 0;
`;

const EditIconHover = styled.img`
  width: 20px;
  height: 20px;
  display: none;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SettingsTagsRow = styled.div`
  padding: 12px 24px 16px;
  border-bottom: 1px solid #c9c6cc80;
  background: #FBF7FE;
  border-radius: 0;
`;

const MainContentSection = styled.div`
  padding: 0 24px;
`;

const PreviewButton = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-radius: 32px;
  border: 1px solid #7200e0;
  gap: 4px;
  background: #ffffff;
  cursor: pointer;
  height: 40px;

  &:hover {
    background-color: #f9f5ff;
  }
`;

const PreviewButtonIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const PreviewButtonText = styled.p`
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  color: #7200e0;
  margin: 0;
`;

const SaveButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 8px 16px;
  border-radius: 32px;
  background: #7200e0;
  cursor: pointer;

  &:hover {
    background-color: rgba(80, 0, 157, 1);
  }

  ${({ $disabled }) =>
    $disabled &&
    `
    background: #c9c6cc;
    cursor: not-allowed;
    pointer-events: none;
  `}
`;

const SaveButtonText = styled.p`
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  color: #ffffff;
  margin: 0;
`;

// Step Section Styles
const StepLabelRow = styled.div`
  padding: 16px 0;
  background: #ffffff;
`;

// Objectives Section Styles
const ObjectivesContainer = styled.div`
  padding: 0 0 20px;
  background: #ffffff;
`;

const SectionTitle = styled.h2`
  font-family: IBM Plex Sans;
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 16px 0;
  color: #1e252a;
`;

const ObjectivesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ObjectiveItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
  position: relative;

  &:last-child {
    border-bottom: none;
  }

  &:hover button {
    opacity: 1;
  }
`;

const ObjectiveText = styled.span`
  flex: 1;
  font-family: IBM Plex Sans;
  font-size: 14px;
  color: #1e252a;
  cursor: pointer;
  padding-right: 40px;

  &:hover {
    background-color: #f8f7f9;
  }
`;

const ObjectiveInput = styled.input`
  flex: 1;
  font-family: IBM Plex Sans;
  font-size: 14px;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  color: #1e252a;

  &::placeholder {
    color: #9e9e9e;
  }
`;

const DeleteButton = styled.button`
  position: absolute;
  right: 0;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #F44336;
  font-size: 18px;
  opacity: 0;
  transition: opacity 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const AddObjectiveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: IBM Plex Sans;
  font-size: 14px;
  font-weight: 500;
  color: #7200E0;
  margin-top: 8px;

  &:hover {
    opacity: 0.7;
  }

  img {
    width: 16px;
    height: 16px;
  }
`;

const BottomButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 20px 0;
  background: #ffffff;
  border-top: 1px solid #e5e5e5;
`;

export default CreateNewSuccessCriteriaDesktop;
